// src/pages/GeneralTest.jsx
"use client";

import React, { useMemo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { translations } from "../../translation/translate";
import LatestResultsHeader from "../../Components/latest-results/LatestResultsHeader";
import LatestResultsTableHeader from "../../Components/latest-results/LatestResultsTableHeader";
import TestGroup from "../../Components/latest-results/TestGroup";
import LatestResultsFooter from "../../Components/latest-results/LatestResultsFooter";
import MultipleCaptureModal from "../../Components/MultipleCaptureModal";
import CoughTestModal from "./CoughTestModal";
import StethoscopeModal from "./StethoscopeModal";
import { useLatestResults } from "../../Hooks/useLatestResults";
import { useEntUpload } from "../../Hooks/useEntUpload";
import { filterTestsForDisplay } from "../../Utils/TestNames";
// import { translations } from "../../translation/translate";

export default function GeneralTest() {
  const navigate = useNavigate();
  const tests = useSelector((s) => s.testSlice.testsData || []);
  const initRef = useSelector((s) => s.testSlice.referedBy || "");
  const lang = useSelector((s) => s.langSlice.langData || "en");
  // console.log(tests, "---tests---");

  const filteredTests = useMemo(() => filterTestsForDisplay(tests), [tests]);

  const {
    // state
    rowLoading,
    cbcLoading,
    routinePanelLoading,
    isModalOpen,
    cameraTarget,
    isStethoscopeOpen,

    // derived
    hasAnyCBC,
    hasRoutinePanel,

    // data bridge
    results,
    referredBy,
    setReferredBy,
    allResultsFilled,
    saveResults,
    handleResultChange,

    // utils
    isCbcParam,
    isCalculated,
    renderUnitRange,
    getEntImages,
    removeEntImage,
    clearEntImages,
    entImageLimit,

    // actions
    openModal,
    closeModal,
    handleCapture,
    handleFetchAllCBC,
    handleFetchResult,
    handleFetchRoutinePanel,

    isCoughOpen,
    openCoughFor,
    closeCough,
    handleCoughValidated,
    isCoughParam,
    isCameraParam,
    isStethoscopeParam,
    openStethoscope,
    closeStethoscope,
    handleStethoscopeResult,
  } = useLatestResults(filteredTests, initRef);

  const { uploadEntImages, entUploading } = useEntUpload();

  // 🔔 New: confirmation modal state
  const [isShareConfirmOpen, setIsShareConfirmOpen] = useState(false);
  const [lastDataConfirm, setLastDataConfirm] = useState(null);

  const entCaptureCount = useMemo(() => {
    if (!cameraTarget || cameraTarget?.ParameterID !== "EN4058") {
      return 0;
    }
    return typeof getEntImages === "function"
      ? getEntImages("EN4058").length
      : 0;
  }, [cameraTarget, getEntImages]);

  const handleFetchResultWithConfirm = useCallback(
    async (detail) => {
      const response = await handleFetchResult(detail);
      if (response?.error === "lastData") {
        setLastDataConfirm({
          parameterId: response?.parameterId || detail?.ParameterID,
          value: response?.value ?? "",
        });
      }
      return response;
    },
    [handleFetchResult]
  );

  const handleFetchAllCBCWithConfirm = useCallback(async () => {
    const response = await handleFetchAllCBC();
    if (
      response?.error === "lastData" &&
      response?.updates &&
      Object.keys(response.updates).length
    ) {
      setLastDataConfirm({
        updates: response.updates,
        count: response.count,
      });
    }
    return response;
  }, [handleFetchAllCBC]);

  const handleFetchRoutinePanelWithConfirm = useCallback(async () => {
    const response = await handleFetchRoutinePanel();
    if (
      response?.error === "lastData" &&
      response?.updates &&
      Object.keys(response.updates).length
    ) {
      setLastDataConfirm({
        updates: response.updates,
        count: response.count,
      });
    }
    return response;
  }, [handleFetchRoutinePanel]);

  const handleConfirmLastData = useCallback(() => {
    if (lastDataConfirm?.updates) {
      const entries = Object.entries(lastDataConfirm.updates);
      if (entries.length) {
        entries.forEach(([parameterId, value]) => {
          handleResultChange(parameterId, value);
        });
      }
      setLastDataConfirm(null);
      return;
    }

    if (lastDataConfirm?.parameterId) {
      handleResultChange(lastDataConfirm.parameterId, lastDataConfirm.value);
    }
    setLastDataConfirm(null);
  }, [handleResultChange, lastDataConfirm]);

  /**
   * 🔹 Runs when user clicks SAVE in footer
   * If ENT images exist, open confirmation modal.
   * If not, just save results (old behaviour).
   */
  const handleSaveClick = useCallback(() => {
    const entImages =
      typeof getEntImages === "function" ? getEntImages("EN4058") : [];
    const hasEntImages = Array.isArray(entImages) && entImages.length > 0;

    if (hasEntImages) {
      setIsShareConfirmOpen(true);
    } else {
      // No ENT images → normal save
      saveResults();
    }
  }, [getEntImages, saveResults]);

  /**
   * 🔹 Runs when user clicks CONFIRM in the modal
   * Uploads ENT images, saves results & redirects to summary.
   */
  const handleConfirmShare = useCallback(async () => {
    const entImages =
      typeof getEntImages === "function" ? getEntImages("EN4058") : [];
    const hasEntImages = Array.isArray(entImages) && entImages.length > 0;

    // Safety: if somehow no images now, fallback to normal save
    if (!hasEntImages) {
      setIsShareConfirmOpen(false);
      saveResults();
      return;
    }

    let entResponse = null;
    try {
      entResponse = await uploadEntImages(entImages);
    } catch (error) {
      // You can show a toast/error here if you want
      console.error("ENT upload failed:", error);
      return;
    }

    setIsShareConfirmOpen(false);

    // Save results without redirecting to main report
    saveResults({ redirectToReport: false });

    // Go to ENT upload summary
    navigate("/ent-upload-summary", {
      state: {
        entResponse,
        entImagesCount: entImages.length,
      },
    });
  }, [getEntImages, uploadEntImages, saveResults, navigate]);

  const hasReferredBy = (referredBy || "").trim() !== "";
  const canSaveResults = allResultsFilled && hasReferredBy && !entUploading;

  return (
    <div className="flex flex-col items-center justify-center h-screen w-screen">
      <div className="w-[80%] h-[85%] bg-white rounded-2xl my-5 mx-auto flex flex-col">
        {/* Header */}
        <LatestResultsHeader />

        {/* Table Header */}
        <LatestResultsTableHeader />

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-4 space-y-6">
          {filteredTests.map((grp) => (
            <TestGroup
              key={grp.name}
              group={grp}
              results={results}
              onChange={handleResultChange}
              isCalculated={isCalculated}
              renderUnitRange={renderUnitRange}
              isCbcParam={isCbcParam}
              rowLoading={rowLoading}
              onFetchOne={handleFetchResultWithConfirm}
              isCoughParam={isCoughParam}
              onOpenCough={openCoughFor}
              isCameraParam={isCameraParam}
              onOpenCamera={openModal}
              isStethoscopeParam={isStethoscopeParam}
              onOpenStethoscope={openStethoscope}
              getEntImages={getEntImages}
              onRemoveEntImage={removeEntImage}
              onClearEntImages={clearEntImages}
              entImageLimit={entImageLimit}
              // ✅ CBC props
              hasAnyCBC={hasAnyCBC}
              cbcLoading={cbcLoading}
              onFetchAllCBC={handleFetchAllCBCWithConfirm}
              hasRoutinePanel={hasRoutinePanel}
              routinePanelLoading={routinePanelLoading}
              onFetchRoutinePanel={handleFetchRoutinePanelWithConfirm}
            />
          ))}
        </div>

        {/* Footer */}
        <LatestResultsFooter
          referredBy={referredBy}
          setReferredBy={setReferredBy}
          hasAnyCBC={hasAnyCBC}
          cbcLoading={cbcLoading}
          onFetchAllCBC={handleFetchAllCBC}
          onSave={handleSaveClick} // 🔁 changed from handleSaveResults
          canSave={canSaveResults}
          isSaving={entUploading}
          translations={translations}
          lang={lang}
        />
      </div>

      {/* Existing camera modal */}
      {isModalOpen && cameraTarget && (
        <MultipleCaptureModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onCapture={handleCapture}
          captureLimit={entCaptureCount}
        />
      )}

      {/* Existing stethoscope modal */}
      {isStethoscopeOpen && (
        <StethoscopeModal
          isOpen={isStethoscopeOpen}
          onClose={closeStethoscope}
          onResult={handleStethoscopeResult}
        />
      )}

      {/* Existing cough modal */}
      <CoughTestModal
        isOpen={isCoughOpen}
        onClose={closeCough}
        title="Cough Analysis"
        onValidated={handleCoughValidated}
      />

      {/* 🔔 New: Share-confirmation modal */}
      {isShareConfirmOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100">
            {/* Header + Icon + Text */}
            <div className="flex items-start gap-3 p-5 border-b border-gray-100">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E7E4FF]">
                {/* Simple shield/lock icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#5B4AE3]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3L5 6v5c0 4.28 2.99 8.17 7 9 4.01-.83 7-4.72 7-9V6l-7-3z" />
                  <path d="M12 11v3" />
                  <circle cx="12" cy="9" r="1.2" />
                </svg>
              </div>

              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900">
                  Share images with your doctor?
                </h2>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                  By clicking <span className="font-semibold">Confirm</span>,
                  you consent to securely share your ENT images with your doctor
                  for medical review and treatment planning.
                </p>
              </div>
            </div>

            {/* Subtext / privacy note */}
            <div className="px-5 pt-3 pb-4">
              <p className="text-xs text-gray-400 leading-relaxed">
                Your images are used only for clinical purposes within this
                consultation.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-5 pb-5">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 transition-colors"
                onClick={() => setIsShareConfirmOpen(false)}
                disabled={entUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-black
             border border-gray-300 hover:bg-[#c8cee5] 
             disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirmShare}
                disabled={entUploading}
              >
                {entUploading ? "Uploading..." : "Confirm & Share"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lastDataConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100">
            <div className="flex items-start gap-3 p-5 border-b border-gray-100">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE9D6]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#D97706]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900">
                  No latest data found
                </h2>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                  Are you sure you want to use the last available data?
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-5">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 transition-colors"
                onClick={() => setLastDataConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-black border border-gray-300 hover:bg-[#FFE9D6]"
                onClick={handleConfirmLastData}
              >
                Use Last Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
